// ENV-006I2: the public Supabase API key used by browser code.
//
// Supabase's modern publishable key (`sb_publishable_…`) replaces the legacy
// `anon` JWT for anything that ships to a browser. Both carry the same low
// privileges, so RLS and Supabase Auth behave identically; only the credential
// format changes.
//
// Production has not been migrated yet, so the legacy variable stays supported:
//
//   REACT_APP_SUPABASE_PUBLISHABLE_KEY  absent -> legacy anon fallback allowed
//                                       present -> it is the key, full stop
//   REACT_APP_SUPABASE_ANON_KEY         fallback while legacy keys remain
//
// ENV-006I2A: "present" is decided by whether the variable exists, not by
// whether it holds anything useful. A publishable variable that exists but is
// blank or whitespace-only FAILS CLOSED rather than quietly selecting the
// legacy anon key. This mirrors the fail-closed rule already enforced for the
// elevated credential in src/utils/serverSupabaseClient.js and
// supabase/functions/_shared/supabase-secret-key.ts: a half-finished migration
// must be a visible outage, not a silent fallback onto a credential that is
// about to be disabled.
//
// ENV-006I2B: `sb_` is a family prefix, not a safety guarantee. The secret key
// (`sb_secret_…`) is the modern service_role equivalent and shares that prefix
// with the publishable key. Only `sb_publishable_…` is a public credential; an
// `sb_secret_…` value found in a REACT_APP_* variable is a misconfiguration
// that must FAIL CLOSED — it is never selected, never falls back onto the
// legacy anon key, and never produces a request header.
//
// Only public credentials belong here. No elevated key is ever read by browser
// code, and src/utils/publicEnvGuard.js fails the build if one is ever placed
// in a REACT_APP_* variable.

const PUBLISHABLE_KEY_PREFIX = 'sb_publishable_';
const SECRET_KEY_PREFIX = 'sb_secret_';
const MODERN_KEY_PREFIX = 'sb_';

function trimText(value) {
  return String(value ?? '').trim();
}

/** Modern publishable keys are opaque strings, not JWTs. */
export function isModernPublicSupabaseKey(key) {
  return trimText(key).startsWith(PUBLISHABLE_KEY_PREFIX);
}

/**
 * ENV-006I2B: the modern elevated credential. Browser code must reject it
 * outright rather than treat it as just another key it does not recognize.
 */
export function isElevatedSupabaseKey(key) {
  return trimText(key).startsWith(SECRET_KEY_PREFIX);
}

/** Any `sb_`-family key is an opaque string rather than a JWT. */
function isOpaqueSupabaseKey(key) {
  return trimText(key).startsWith(MODERN_KEY_PREFIX);
}

/**
 * Pure selection step, so precedence is testable without CRA's build-time
 * inlining of process.env.
 *
 * Absence is `undefined` or `null` — Create React App omits an unset
 * REACT_APP_* name from the inlined environment entirely, so an unset variable
 * reads as undefined while a variable set to "" reads as an empty string.
 */
export function selectBrowserSupabaseKey({ publishableKey, anonKey } = {}) {
  const selected =
    publishableKey === undefined || publishableKey === null
      ? trimText(anonKey)
      : trimText(publishableKey);

  // ENV-006I2B: an elevated key is not a usable public credential, whichever
  // variable it was pasted into. Resolving to "" is the same visible outage a
  // blank publishable variable produces — deliberately not an anon fallback,
  // which would mask the misconfiguration behind a working page.
  if (isElevatedSupabaseKey(selected)) {
    return '';
  }

  return selected;
}

/**
 * Reads the public key from the build environment.
 *
 * Both names are referenced as literal `process.env.REACT_APP_*` member
 * expressions so Create React App inlines them at build time. No elevated
 * variable name is read here, or anywhere else in browser code.
 */
export function getBrowserSupabaseKey() {
  return selectBrowserSupabaseKey({
    publishableKey: process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY,
    anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY,
  });
}

/**
 * Headers for a direct fetch to a public Edge Function endpoint.
 *
 * A legacy anon key is a JWT and is duplicated onto `Authorization` exactly as
 * before, which is what supabase-js itself does. A modern key is opaque and
 * travels on `apikey` only: the platform tries to parse a `Bearer sb_…` value
 * as a JWT and rejects it.
 *
 * ENV-006I2B: an elevated key produces no headers at all, so a misconfigured
 * build cannot put a secret key on the wire from a browser.
 *
 * Neither public form authorizes anything on its own. Endpoints that identify
 * a caller read a real Supabase Auth session token, which is unchanged.
 */
export function buildPublicSupabaseFunctionHeaders(key = getBrowserSupabaseKey()) {
  const publicKey = trimText(key);
  if (!publicKey || isElevatedSupabaseKey(publicKey)) {
    return {};
  }

  const headers = { apikey: publicKey };

  if (!isOpaqueSupabaseKey(publicKey)) {
    headers.Authorization = `Bearer ${publicKey}`;
  }

  return headers;
}
